/**
 * instructionsSlice — instructions step payload (meeting, CT, extras).
 */

import type { StateCreator } from 'zustand'

import type { MeetingDTO, SalesforceId } from 'models/purchaseFlowRevamp'
import type { NextStepValidationError } from 'models/purchaseFlow/nextStepValidationError'
import type { PfStoreState } from '../store'

export interface InstructionsSliceState {
  meeting?: MeetingDTO
  /**
   * Is the phone number currently shown in the meeting form invalid?
   *
   * The intl-tel input is the only thing that can answer this (country-aware
   * libphonenumber), so the section reports it up and the Next gate reads it,
   * exactly as legacy plumbed `setPhoneInputValidationError` into
   * `areCurrentMeetingFieldsValid`. Without it the input renders an error while
   * the step happily continues.
   *
   * UX state, NOT payload: absent from `serializeDraft` and the autosave
   * fingerprint, and never written by the setter as a dirtying change — the
   * precedent is `extrasFromInstructions` (MODULE-CONTRACT §11.2). `undefined`
   * means "not evaluated yet" and must never block: a resumed draft is judged
   * only once the input has re-mounted and re-validated.
   */
  meetingPhoneInvalid?: boolean
  /**
   * Is the chosen meeting date earlier than the lead-time floor?
   *
   * The floor depends on the instruction catalogue's timezone, which is React
   * Query data the gate cannot reach, so the page derives the verdict and the
   * gate reads it — the same seam `meetingPhoneInvalid` uses, and for the same
   * reason. UX state: not serialized, not in the autosave fingerprint, never
   * dirtying. `undefined`/false never blocks.
   */
  meetingDateTooSoon?: boolean
  /** Is the chosen meeting date past the latest bookable day? Derived by the page, like `meetingDateTooSoon`. */
  meetingDateTooFar?: boolean
  /** A fixed booking has a day and an hour but no minutes yet. Set by the page, like `meetingDateTooSoon`. */
  meetingMinutesMissing?: boolean
  /** Only one access mode is offered, so its card shows as chosen before anything is typed. Set by the page. */
  meetingModeImplied?: boolean
  /** A key pickup's travel cost is not quoted yet (or the quote failed). Set by the page; never dirtying. */
  keyPickupTravelQuotePending?: boolean
  /**
   * A fee the order will bill is not priced yet: its creatives list or travel quote is still loading or
   * failed. The payment would charge less than the order bills, so Place order waits. Set by
   * `useInstructionFeesMirror`; never dirtying.
   */
  instructionFeesUnknown?: boolean
  ctBookingId?: string
  /** The chosen creative's name, kept with the id so the Summary can say who was booked. */
  ctBookingName?: string
  extraServices: SalesforceId[]
  comments?: string
  scheduledByCT?: boolean
  /** Fixed or flexible booking, kept so a reload or a resumed draft reopens on the client's choice. */
  scheduleMethod?: 'fixed' | 'flexible'
  /**
   * The gaps a click on the disabled Next button named. The page outlines each one in red while it is still open.
   * UX state: not serialized, never dirtying.
   */
  revealedInstructionsGaps: NextStepValidationError[]
}

export interface InstructionsSliceActions {
  setMeeting(meeting: MeetingDTO | undefined): void
  setMeetingPhoneInvalid(invalid: boolean | undefined): void
  setMeetingDateTooSoon(tooSoon: boolean | undefined): void
  setMeetingDateTooFar(tooFar: boolean | undefined): void
  setMeetingMinutesMissing(missing: boolean | undefined): void
  setMeetingModeImplied(implied: boolean | undefined): void
  setKeyPickupTravelQuotePending(pending: boolean | undefined): void
  setInstructionFeesUnknown(unknown: boolean): void
  setCtBookingId(id: string | undefined, name?: string): void
  setExtraServices(ids: SalesforceId[]): void
  setComments(comments: string | undefined): void
  setScheduledByCT(flag: boolean): void
  setScheduleMethod(method: 'fixed' | 'flexible'): void
  revealInstructionsGaps(gaps: NextStepValidationError[]): void
  resetInstructions(): void
}

export type InstructionsSlice = InstructionsSliceState & InstructionsSliceActions

const initialState: InstructionsSliceState = {
  meeting: undefined,
  meetingPhoneInvalid: undefined,
  meetingDateTooSoon: undefined,
  meetingDateTooFar: undefined,
  meetingMinutesMissing: undefined,
  meetingModeImplied: undefined,
  keyPickupTravelQuotePending: undefined,
  instructionFeesUnknown: undefined,
  ctBookingId: undefined,
  ctBookingName: undefined,
  extraServices: [],
  comments: undefined,
  scheduledByCT: undefined,
  scheduleMethod: undefined,
  revealedInstructionsGaps: [],
}

export const createInstructionsSlice: StateCreator<PfStoreState, [], [], InstructionsSlice> = (set) => ({
  ...initialState,

  setMeeting: (meeting) => set((state) => ({
    meeting,
    // A mode switch shows a different phone field, so the verdict on the old
    // one must not survive to block the new mode.
    meetingPhoneInvalid: meeting?.mode === state.meeting?.mode ? state.meetingPhoneInvalid : undefined,
    draft: { ...state.draft, dirty: true },
  })),

  // No `dirty` — this is the input's verdict on what the user typed, not a
  // change to the order. Marking it dirty would autosave a draft on every
  // keystroke that flips validity.
  setMeetingPhoneInvalid: (meetingPhoneInvalid) => set({ meetingPhoneInvalid }),

  setMeetingDateTooSoon: (meetingDateTooSoon) => set({ meetingDateTooSoon }),

  setMeetingDateTooFar: (meetingDateTooFar) => set({ meetingDateTooFar }),

  setMeetingMinutesMissing: (meetingMinutesMissing) => set({ meetingMinutesMissing }),

  setMeetingModeImplied: (meetingModeImplied) => set({ meetingModeImplied }),

  revealInstructionsGaps: (revealedInstructionsGaps) => set({ revealedInstructionsGaps }),

  setKeyPickupTravelQuotePending: (keyPickupTravelQuotePending) => set({ keyPickupTravelQuotePending }),

  setInstructionFeesUnknown: (instructionFeesUnknown) => set({ instructionFeesUnknown }),

  setCtBookingId: (ctBookingId, ctBookingName) => set((state) => ({
    ctBookingId,
    ctBookingName: ctBookingId ? ctBookingName : undefined,
    draft: { ...state.draft, dirty: true },
  })),

  setExtraServices: (extraServices) => set((state) => ({
    extraServices,
    draft: { ...state.draft, dirty: true },
  })),

  setComments: (comments) => set((state) => ({
    comments,
    draft: { ...state.draft, dirty: true },
  })),

  setScheduledByCT: (scheduledByCT) => set((state) => ({
    scheduledByCT,
    // Turning CT scheduling ON drops any in-progress meeting. The mode
    // picker + per-mode fields are hidden while `scheduledByCT`, so a stale
    // partial meeting would (a) wedge the completeness gate, which still
    // validates meeting fields whenever a `meeting` object is present, and
    // (b) leak a contradictory `organizationMethod` onto the wire —
    // `serializePlacement` reads `state.meeting` directly and ignores
    // `scheduledByCT`. Clearing here keeps the slice the single source of truth.
    meeting: scheduledByCT ? undefined : state.meeting,
    draft: { ...state.draft, dirty: true },
  })),

  setScheduleMethod: (scheduleMethod) => set((state) => (
    state.scheduleMethod === scheduleMethod ? state : { scheduleMethod, draft: { ...state.draft, dirty: true } }
  )),

  resetInstructions: () => set((state) => ({
    ...initialState,
    draft: { ...state.draft, dirty: true },
  })),
})
